import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  OnInit,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faSave,
  faPlus,
  faEdit,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import {
  ClassicEditor,
  EditorConfig,
  Essentials,
  Bold,
  Italic,
  Link,
  List,
  Undo,
  Heading,
} from "ckeditor5";
import { DateTime } from "luxon";
import { startWith, map } from "rxjs";

import { ScheduledEmail } from "src/app/api/models/scheduled-email";
import { LocaleService } from "src/app/shared/services/locale.service";
import { DateTimePickerComponent } from "src/app/shared/components/datetime-picker/datetime-picker.component";
import { MeetdayModalFacade } from "../meetday-modal.facade";
import { MeetdayTabEmailsService } from "./meetday-tab-emails.service";

const CKEDITOR_INIT_DELAY_MS = 100;

@Component({
  selector: "app-meetday-tab-emails",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    CKEditorModule,
    DateTimePickerComponent,
  ],
  providers: [MeetdayTabEmailsService],
  template: `
    <h3>{{ "Scheduled Emails" | translate }}</h3>

    <!-- Add New Email Button -->
    @if (!isEditingEmail()) {
    <div class="mb-3">
      <button class="btn btn-primary" (click)="createNewEmail()">
        <fa-icon [icon]="faPlus"></fa-icon>
        {{ "Add New Email" | translate }}
      </button>
    </div>
    }

    <!-- Email Form (shown when editing) -->
    @if (isEditingEmail()) {
    <div class="mb-4 p-3 border rounded" [formGroup]="emailService.emailForm">
      <h4>
        {{
          selectedEmail()
            ? ("Edit Email" | translate)
            : ("New Email" | translate)
        }}
      </h4>

      <div class="form-group">
        <label>{{ "Subject" | translate }}*</label>
        <input
          type="text"
          class="form-control"
          formControlName="emailSubject"
          [placeholder]="'Enter email subject' | translate"
          required
        />
      </div>

      <div class="form-group">
        <label>{{ "Scheduled Date & Time" | translate }}*</label>
        <app-datetime-picker
          formControlName="scheduledDateTime"
          mode="datetime"
        />
        @if (emailTypeDisplay()) {
        <small class="form-text text-muted">
          {{ "Type" | translate }}:
          <strong>{{ emailTypeDisplay() | translate }}</strong>
        </small>
        }
      </div>

      <div class="form-group">
        <label>{{ "Message Body" | translate }}*</label>
        <div class="mb-3">
          @if (isEditingEmail()) {
          <ckeditor
            [editor]="Editor"
            [config]="editorConfig"
            (change)="updateEmailBody($event)"
            (ready)="onEditorReady($event)"
          >
          </ckeditor>
          }
        </div>
      </div>

      <div class="btn-group">
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="!isEmailFormValid()"
          (click)="onSaveEmail()"
        >
          <fa-icon [icon]="faSave"></fa-icon>
          {{ "Save" | translate }}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          (click)="cancelEmailEditing()"
        >
          <fa-icon [icon]="faTimes"></fa-icon>
          {{ "Cancel" | translate }}
        </button>
      </div>
    </div>
    }

    <!-- Emails List Table -->
    @if (!isEditingEmail()) {
    <div>
      @if (emailsForMeetDay().length > 0) {
      <table class="w-100 table table-striped">
        <thead>
          <tr>
            <th>{{ "Subject" | translate }}</th>
            <th>{{ "Scheduled Date/Time" | translate }}</th>
            <th>{{ "Type" | translate }}</th>
            <th>{{ "Relative Time" | translate }}</th>
            <th class="text-right">{{ "Actions" | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (email of emailsForMeetDay(); track email.id) {
          <tr>
            <td>{{ email.emailSubject }}</td>
            <td>{{ email.scheduledDateTime | date : "short" }}</td>
            <td>
              <span
                class="badge"
                [class.badge-warning]="
                  getEmailTypeDisplay(email) === 'Reminder'
                "
                [class.badge-info]="getEmailTypeDisplay(email) === 'Follow-up'"
              >
                {{ getEmailTypeDisplay(email) | translate }}
              </span>
            </td>
            <td>{{ getRelativeTimeDisplay(email) }}</td>
            <td class="text-right">
              <button
                class="btn btn-sm btn-warning mr-2"
                type="button"
                (click)="selectEmailForEditing(email)"
              >
                <fa-icon [icon]="faEdit"></fa-icon>
                {{ "Edit" | translate }}
              </button>
              <button
                class="btn btn-sm btn-danger"
                type="button"
                (click)="onDeleteEmail(email.id!)"
                [disabled]="!email.id"
              >
                <fa-icon [icon]="faTimes"></fa-icon>
                {{ "Delete" | translate }}
              </button>
            </td>
          </tr>
          }
        </tbody>
      </table>
      }

      <!-- No Emails Message -->
      @if (emailsForMeetDay().length === 0) {
      <div class="alert alert-info">
        {{ "No scheduled emails for this meet day yet" | translate }}
      </div>
      }
    </div>
    }
  `,
})
export class MeetdayTabEmailsComponent implements OnInit {
  private locale = inject(LocaleService);

  // Icons
  faSave = faSave;
  faPlus = faPlus;
  faEdit = faEdit;
  faTimes = faTimes;

  // Services
  facade = inject(MeetdayModalFacade);
  emailService = inject(MeetdayTabEmailsService);

  // LOCAL UI state
  isEditingEmail = signal<boolean>(false);
  selectedEmail = signal<ScheduledEmail | null>(null);
  isEditorReady = signal<boolean>(false);
  editorInstance = signal<any>(null);

  // CKEditor configuration
  Editor = ClassicEditor;
  editorConfig: EditorConfig = {
    language: this.locale.getCurrentLanguage(),
    licenseKey: "GPL",
    plugins: [Heading, Essentials, Bold, Italic, Link, List, Undo],
    toolbar: [
      "heading",
      "|",
      "bold",
      "italic",
      "|",
      "link",
      "|",
      "bulletedList",
      "numberedList",
      "|",
      "undo",
      "redo",
    ],
    placeholder: "Enter your email message here...",
  };

  // Local computed - filter emails for this meetday
  emailsForMeetDay = computed(() => {
    const meetDayId = this.facade.entity().id;
    return this.emailService
      .scheduledEmails()
      .filter((email) => email.meetDayId === meetDayId);
  });

  // Reactive form validity using statusChanges
  private formStatus = toSignal(
    this.emailService.emailForm.statusChanges.pipe(
      startWith(this.emailService.emailForm.status),
      map((status) => status === "VALID")
    ),
    { initialValue: false }
  );
  isEmailFormValid = computed(() => this.formStatus());

  emailTypeDisplay = computed(() => {
    // Form now stores ISO directly via DateTimePickerComponent
    const isoDateTime =
      this.emailService.emailForm.get("scheduledDateTime")?.value;
    return this.determineEmailType(isoDateTime, this.facade.entity().date);
  });

  ngOnInit(): void {
    // Load emails when tab is initialized
    this.emailService.loadScheduledEmails();
  }

  // UI Methods
  createNewEmail(): void {
    this.selectedEmail.set(null);
    this.isEditingEmail.set(true);
    this.isEditorReady.set(false);

    setTimeout(() => {
      this.resetEmailForm();
      this.setEditorContent("");
    }, CKEDITOR_INIT_DELAY_MS);
  }

  selectEmailForEditing(email: ScheduledEmail): void {
    this.selectedEmail.set(email);
    this.isEditingEmail.set(true);
    this.isEditorReady.set(false);

    setTimeout(() => {
      // DateTimePickerComponent accepts ISO directly
      this.emailService.emailForm.patchValue({
        emailSubject: email.emailSubject || "",
        emailBody: email.emailBody || "",
        scheduledDateTime: email.scheduledDateTime || null,
      });

      this.setEditorContent(email.emailBody || "");
    }, CKEDITOR_INIT_DELAY_MS);
  }

  cancelEmailEditing(): void {
    this.isEditingEmail.set(false);
    this.selectedEmail.set(null);
    this.resetEmailForm();
  }

  onSaveEmail(): void {
    if (!this.isEmailFormValid()) {
      return;
    }

    const form = this.emailService.emailForm.value;
    const selectedEmail = this.selectedEmail();
    const meetDayId = this.facade.entity().id;

    // Form stores ISO directly via DateTimePickerComponent
    const emailData: ScheduledEmail = {
      id: selectedEmail?.id,
      emailSubject: form.emailSubject,
      emailBody: form.emailBody,
      scheduledDateTime: form.scheduledDateTime,
      meetDayId: meetDayId,
    };

    const isNew = !selectedEmail?.id;
    this.emailService.saveEmail(emailData, isNew).subscribe();

    this.cancelEmailEditing();
  }

  onDeleteEmail(emailId: string): void {
    this.emailService.deleteScheduledEmail(emailId);
  }

  // Editor handlers
  onEditorReady(editor: any): void {
    this.editorInstance.set(editor);
    this.isEditorReady.set(true);

    const currentBody = this.emailService.emailForm.get("emailBody")?.value;
    if (currentBody) {
      this.setEditorContent(currentBody);
    }
  }

  updateEmailBody(event: any): void {
    let content = "";
    if (event && typeof event.editor !== "undefined") {
      content = event.editor.getData();
    } else if (typeof event === "string") {
      content = event;
    } else if (event?.target?.value) {
      content = event.target.value;
    }

    this.emailService.emailForm.patchValue({ emailBody: content });
  }

  // Helper methods
  getEmailTypeDisplay(email: ScheduledEmail): string {
    return this.determineEmailType(
      email.scheduledDateTime,
      this.facade.entity().date
    );
  }

  getRelativeTimeDisplay(email: ScheduledEmail): string {
    const meetDayDate = this.facade.entity().date;

    if (!email.scheduledDateTime || !meetDayDate) {
      return "";
    }

    // Parse as UTC since backend returns times without timezone suffix
    const scheduledDate = DateTime.fromISO(email.scheduledDateTime, {
      zone: "utc",
    });
    const eventDate = DateTime.fromISO(meetDayDate, { zone: "utc" });

    if (!scheduledDate.isValid || !eventDate.isValid) {
      return "";
    }

    const diffDays = Math.abs(
      Math.round(scheduledDate.diff(eventDate, "days").days)
    );
    const emailType = this.getEmailTypeDisplay(email);

    return emailType === "Reminder"
      ? `${diffDays} day${diffDays !== 1 ? "s" : ""} before event`
      : `${diffDays} day${diffDays !== 1 ? "s" : ""} after event`;
  }

  private resetEmailForm(): void {
    this.emailService.resetForm();
  }

  private setEditorContent(content: string): void {
    const editor = this.editorInstance();
    if (editor && this.isEditorReady()) {
      try {
        editor.setData(content || "");
      } catch (error) {
        console.warn("Could not set editor data:", error);
      }
    }
  }

  private determineEmailType(
    scheduledDateTime: string | undefined,
    meetDayDate: string | undefined
  ): string {
    if (!scheduledDateTime || !meetDayDate) {
      return "";
    }

    // Parse as UTC since backend returns times without timezone suffix
    const scheduledDate = DateTime.fromISO(scheduledDateTime, { zone: "utc" });
    const eventDate = DateTime.fromISO(meetDayDate, { zone: "utc" });

    if (!scheduledDate.isValid || !eventDate.isValid) {
      return "";
    }

    return scheduledDate < eventDate ? "Reminder" : "Follow-up";
  }
}
